import React from 'react';
import { Capacitor } from '@capacitor/core';
import { useGridApiContext } from '@mui/x-data-grid-premium';
import { Toolbar, ToolbarButton } from '@mui/x-data-grid-premium';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import Tooltip from '@mui/material/Tooltip';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { styled } from '@mui/material/styles';
import {
  QuickFilter,
  QuickFilterTrigger,
  QuickFilterControl,
  QuickFilterClear,
  GridPreferencePanelsValue,
} from '@mui/x-data-grid-premium';
import MobileExportButton from '@/components/MobileFilesExport';

export type CustomToolbarForNativeProps = {
  showQuickFilter?: boolean;
  showColumns?: boolean;
  showFilters?: boolean;
  excelWorker?: () => Worker;
};

type OwnerState = { expanded: boolean };

const StyledQuickFilter = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center',
});

const StyledTextField = styled(TextField)<{ ownerState: OwnerState }>(
  ({ theme, ownerState }) => ({
    gridArea: '1 / 1',
    overflowX: 'clip',
    width: ownerState.expanded ? 200 : 'var(--trigger-width)',
    opacity: ownerState.expanded ? 1 : 0,
    transition: theme.transitions.create(['width', 'opacity']),
  }),
);

export default function CustomToolbarForNative({
  showQuickFilter,
  showColumns = true,
  showFilters = true,
}: CustomToolbarForNativeProps) {
  const apiRef = useGridApiContext();

  const openColumns = React.useCallback(() => {
    apiRef.current.showPreferences(GridPreferencePanelsValue.columns);
  }, [apiRef]);
  const openFilters = React.useCallback(() => {
    apiRef.current.showPreferences(GridPreferencePanelsValue.filters);
  }, [apiRef]);

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const isNative = Capacitor.getPlatform && Capacitor.getPlatform() !== 'web';
  if (!isNative) return null;

  return (
    <Toolbar>
      {showColumns && (
        <ToolbarButton
          aria-label='columns'
          onClick={openColumns}
        >
          <ViewColumnIcon fontSize='small' />
        </ToolbarButton>
      )}
      {showFilters && (
        <ToolbarButton
          aria-label='filters'
          onClick={openFilters}
        >
          <FilterListIcon fontSize='small' />
        </ToolbarButton>
      )}
      <MobileExportButton />
      {showQuickFilter && (
        <StyledQuickFilter>
          <QuickFilterTrigger
            render={(
              { material: _buttonMaterial, ref: triggerRef, ...triggerProps },
              state,
            ) => (
              <Tooltip
                title='Search'
                enterDelay={0}
              >
                <ToolbarButton
                  {...triggerProps}
                  ref={(element) => {
                    if (typeof triggerRef === 'function') {
                      triggerRef(element);
                    }
                  }}
                  aria-label='Search'
                  aria-disabled={state.expanded}
                  style={{
                    gridArea: '1 / 1',
                    width: 'min-content',
                    height: 'min-content',
                    zIndex: 1,
                    opacity: state.expanded ? 0 : 1,
                    pointerEvents: state.expanded ? 'none' : 'auto',
                    transition: 'opacity 220ms ease',
                  }}
                >
                  <SearchIcon fontSize='small' />
                </ToolbarButton>
              </Tooltip>
            )}
          />
          <QuickFilterControl
            render={({ ref, ...controlProps }, state) => (
              <StyledTextField
                {...controlProps}
                ownerState={{ expanded: state.expanded }}
                inputRef={ref}
                aria-label='Search'
                placeholder='Search...'
                size='small'
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon fontSize='small' />
                      </InputAdornment>
                    ),
                    endAdornment: state.value ? (
                      <InputAdornment position='end'>
                        <QuickFilterClear
                          edge='end'
                          size='small'
                          aria-label='Clear search'
                          style={{ marginRight: -0.75 }}
                        >
                          <CancelIcon fontSize='small' />
                        </QuickFilterClear>
                      </InputAdornment>
                    ) : null,
                    ...controlProps.slotProps?.input,
                  },
                  ...controlProps.slotProps,
                }}
              />
            )}
          />
        </StyledQuickFilter>
      )}
    </Toolbar>
  );
}
